import { BarList } from '@/components/Chart/BarList';
import { LineChart } from '@/components/Chart/LineChart';
import type { BrandCharts as Charts } from '@/features/reports/api';
import {
    chartEmptyNote,
    monthLabel,
    trendBaseline,
    trendNote,
} from '@/features/reports/brands/labels';
import { BRANDS_COPY } from '@/features/reports/copy';
import { ReportCard } from '@/features/reports/parts';

const COPY = BRANDS_COPY.charts;
const count = (value: number) => value.toLocaleString('en-GB');

/**
 * Brand intelligence's four charts under the tiles (ALS-92). Three reads of the quarter on one row,
 * all in the one hue every list on the screen shares, as the prototype draws them: one measure down
 * a labelled list needs no colour of its own. Views and orders by category, then the pieces
 * bought by size, in the register's order rather than ranked, and by colour group. The interest
 * trend is alone on the row under them, long rather than squeezed, its axis starting just below its
 * lowest point because it is an index. Every figure is the API's; nothing here counts. The row
 * stacks on a phone and a tablet.
 */
export function BrandCharts({ charts }: { charts: Charts }) {
    const indexes = charts.trend.map((p) => p.index);

    return (
        <div className="flex min-w-0 flex-col gap-(--layout-row-gap)">
            <div className="grid min-w-0 gap-(--layout-row-gap) lg:grid-cols-3">
                <ReportCard title={COPY.category.title} subtitle={COPY.category.subtitle}>
                    <BarList
                        items={charts.byCategory}
                        format={count}
                        emptyNote={COPY.category.empty}
                    />
                </ReportCard>
                <ReportCard title={COPY.sizes.title} subtitle={COPY.sizes.subtitle}>
                    <BarList
                        items={charts.sizes.bars}
                        format={count}
                        emptyNote={chartEmptyNote(charts.sizes, COPY.sizes.empty)}
                    />
                    <Unrecorded pieces={charts.sizes.unrecorded} say={COPY.sizes.unrecorded} />
                </ReportCard>
                <ReportCard title={COPY.colours.title} subtitle={COPY.colours.subtitle}>
                    <BarList
                        items={charts.colours.bars}
                        format={count}
                        emptyNote={chartEmptyNote(charts.colours, COPY.colours.empty)}
                    />
                    <Unrecorded pieces={charts.colours.unrecorded} say={COPY.colours.unrecorded} />
                </ReportCard>
            </div>

            <ReportCard title={COPY.trend.title} subtitle={trendNote(charts.trend)}>
                {charts.trend.length > 0 ? (
                    <LineChart
                        title={COPY.trend.title}
                        seriesLabel={COPY.trend.series}
                        labels={charts.trend.map((p) => monthLabel(p.month))}
                        values={indexes}
                        baseline={trendBaseline(indexes)}
                        format={count}
                        height={160}
                    />
                ) : (
                    <p className="text-muted-foreground py-4 text-sm">{COPY.trend.empty}</p>
                )}
            </ReportCard>
        </div>
    );
}

/**
 * How many pieces a purchase chart left out for having no size or colour on record (Franka, 30 Oct
 * 2026), so its bars are not read as every piece bought. Nothing when none were.
 */
function Unrecorded({ pieces, say }: { pieces: number; say: (pieces: number) => string }) {
    return pieces > 0 ? <p className="text-muted-foreground mt-3 text-xs">{say(pieces)}</p> : null;
}
